import * as React from 'react';
import { graphql } from 'gatsby';
import { Link, useI18next } from 'gatsby-plugin-react-i18next';
import { Hero } from '@/components/hero/hero';
import { Button, Icon, LazyYouTube, Metadata } from '@/components';
import { Routes } from '@/types';

const FarSightednessHypermetropia = () => {
  const { t, language } = useI18next();

  return (
    <>
      <Hero title={t('g.hypermetropia')} backgroundImage="/img/far-sightedness-main.webp" />
      <div className="container-fluid container-fluid--custom">
        <div className="row">
          <div className="col-12 col-lg-6 p-4">
            <h2 className="mb-5">{t('hyperopia.left-heading.1')}</h2>
            <p>{t('hyperopia.left-body.1')}</p>
            <div className="d-flex mt-5">
              <div className="w-50 pe-2">
                <h5>{t('hyperopia.left-video-heading.1')}</h5>
                <div className="mt-3">
                  <LazyYouTube id={'e7cLzRbml2k'} title={t('hyperopia.left-video-heading.1')} />
                </div>
              </div>
              <div className="w-50 ps-2">
                <h5>{t('hyperopia.left-video-heading.2')}</h5>
                <div className="mt-3">
                  <LazyYouTube id={'WFCvFpjLpjE'} title={t('hyperopia.left-video-heading.2')} />
                </div>
              </div>
            </div>
            <p className="mt-5">{t('hyperopia.left-video-body.1')}</p>
            <p>{t('hyperopia.left-video-body.2')}</p>
            <h2 className="my-5">{t('hyperopia.left-heading.2')}</h2>
            <p>{t('hyperopia.left-body.2-1')}</p>
            <img
              src={`/img/${language}-far-sightedness.webp`}
              loading="lazy"
              alt={t('g.nearsight')}
              className="w-100"
            />
            <p>{t('hyperopia.left-body.2-2')}</p>
            <h2 className="my-5">{t('hyperopia.left-heading.3')}</h2>
            <p>{t('hyperopia.left-subheading.3-1')}</p>
            <ul className="list-unstyled mb-5">
              {t('hyperopia.left-list.3-1')
                .split('\n\n')
                .map((line, index) => (
                  <li key={index} className="my-2">
                    <Icon name="check" size={30} className="me-2" />
                    {line}
                  </li>
                ))}
            </ul>
            <p>{t('hyperopia.left-subheading.3-2')}</p>
            <ul className="list-unstyled mb-5">
              {t('hyperopia.left-list.3-3')
                .split('\n\n')
                .map((line, index) => (
                  <li key={index} className="my-2">
                    <Icon name="check" size={30} className="me-2" />
                    {line}
                  </li>
                ))}
            </ul>
            <h2 className="my-5">{t('hyperopia.left-heading.4')}</h2>
            <p>{t('hyperopia.left-subheading.4')}</p>
            <ul className="list-unstyled mb-5">
              {t('hyperopia.left-list.4')
                .split('\n\n')
                .map((line, index) => (
                  <li key={index} className="my-2">
                    <Icon name="check" size={30} className="me-2" />
                    {line}
                  </li>
                ))}
            </ul>
          </div>
          <div className="col-12 col-lg-6 p-0">
            <div className="bg-gray-200 p-4">
              <h2 className="mb-5">{t('hyperopia.right-heading.1')}</h2>
              <p>
                {t('hyperopia.right-body.1-1')}{' '}
                <Link to={Routes.LASER_VISION}>{t('hyperopia.laser-correction-link')}</Link>.
              </p>
              <p>{t('hyperopia.right-body.1-2')}</p>
            </div>
            <div className="bg-default-green text-white p-4">
              <h2 className="mb-5">{t('hyperopia.right-heading.2')}</h2>
              <p>{t('hyperopia.right-body.2')}</p>
            </div>
            <div className="bg-gray-200 p-4">
              <img
                src="/img/far-sightedness-correction3.webp"
                loading="lazy"
                alt={t('g.nearsight')}
                className="w-100"
              />
              <img
                src="/img/far-sightedness-correction.webp"
                loading="lazy"
                alt={t('g.nearsight')}
                className="w-100 mt-4"
              />

              <h2 className="my-5">{t('hyperopia.right-heading.3')}</h2>
              <p>{t('hyperopia.right-body.3')}</p>
              <ul className="list-unstyled my-5">
                <li className="my-2 d-flex flex-row flex-shrink-0">
                  <div>
                    <h5>{t('hyperopia.right-subheading.3-1')}</h5>
                    <p>{t('hyperopia.right-body.3-1')}</p>
                    <Link to={Routes.MYOPIA}>
                      <Button title={t('g.learn-more')} className="text-uppercase shadow btn-success" />
                    </Link>
                  </div>
                </li>
                <li className="mt-5 my-2 d-flex flex-row flex-shrink-0">
                  <div>
                    <h5>{t('hyperopia.right-subheading.3-2')}</h5>
                    <p>{t('hyperopia.right-body.3-2')}</p>
                    <Link to={Routes.ASTIGMATISM}>
                      <Button title={t('g.learn-more')} className="text-uppercase shadow btn-success" />
                    </Link>
                  </div>
                </li>
              </ul>
              <img src="/img/far-sightedness-exam.webp" loading="lazy" alt={t('g.nearsight')} className="w-100 mt-4" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const Head = (props) => {
  const { data, pageContext } = props;
  const locale = JSON.parse(data?.locales?.edges?.[0]?.node?.data || null);

  return (
    <Metadata
      title={locale?.hyperopia?.title}
      description={locale?.hyperopia?.description}
      fallbackTitle={locale?.g.title}
      pageContext={pageContext}
      locale={locale}
    />
  );
};

export default FarSightednessHypermetropia;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
